import { useEffect } from 'react';
import { fetchQuery, useRelayEnvironment } from 'react-relay/hooks';

import { graphql } from 'babel-plugin-relay/macro';
import { useLiveRef } from 'ui-utils';

import {
  useNodesCountQuery,
  useNodesCountQueryResponse,
  useNodesCountQueryVariables,
} from './__generated__/useNodesCountQuery.graphql';

export default function useNodesCount(
  variables: useNodesCountQueryVariables,
  onSuccess: (counts: useNodesCountQueryResponse | undefined) => void,
  shouldFetch: boolean = true,
) {
  const enviroment = useRelayEnvironment();

  const onSuccessRef = useLiveRef(onSuccess);
  // 👇🏽 destructure variables here to pass to the useEffect
  // because `variables` has a unstable reference
  // and we only care about the boolean values inside
  const {
    includeProvider,
    includeGroup,
    includeReferringProvider,
    includeRenderingProvider,
    includeFacility,
    includeCustomAction,
    includeApptType,
    includeCCDAForms,
    includeForms,
    includeSchedulingPlus,
    includeFormName,
    includeInsurance,
    includeSender,
    includeApprover,
    includeSchedulerProvider,
    includeTrueTimeProvider,
  } = variables;

  /**
   * We have some filters that only uses data on REST, for those we don't want to call an empty GraphQl query
   */
  const isToQuery = Object.values(variables).some(Boolean);

  useEffect(() => {
    if (isToQuery && shouldFetch) {
      const stable_onSuccess = onSuccessRef.current;
      const queryObservable = fetchQuery<useNodesCountQuery & { user: number }>(
        enviroment,
        graphql`
          query useNodesCountQuery(
            $includeProvider: Boolean = false
            $includeRenderingProvider: Boolean = false
            $includeReferringProvider: Boolean = false
            $includeFacility: Boolean = false
            $includeCustomAction: Boolean = false
            $includeApptType: Boolean = false
            $includeCCDAForms: Boolean = false
            $includeForms: Boolean = false
            $includeSchedulingPlus: Boolean = false
            $includeFormName: Boolean = false
            $includeInsurance: Boolean = false
            $includeGroup: Boolean = false
            $includeSender: Boolean = false
            $includeApprover: Boolean = false
            $includeSchedulerProvider: Boolean = false
            $includeTrueTimeProvider: Boolean = false
          ) {
            provider: providerCount(
              filter: { visible: true, schedulable: true, isReferringProvider: false, type: doctor }
            ) @include(if: $includeProvider)
            providers: providerCount(filter: { visible: true, type: doctor }) @include(if: $includeProvider)
            renderingProvider: providerCount(
              filter: { visible: true, isReferringProvider: false, isRenderingProvider: true, type: doctor }
            ) @include(if: $includeRenderingProvider)
            referringProvider: providerCount(
              filter: { visible: true, isReferringProvider: true, isRenderingProvider: false, type: doctor }
            ) @include(if: $includeReferringProvider)
            facility: facilityCount(filter: { visible: true }) @include(if: $includeFacility)
            customAction: followupCount(filter: { enabled: true }) @include(if: $includeCustomAction)
            apptType: appointmentTypeCount(filter: { visible: true }) @include(if: $includeApptType)
            forms: patientFormTemplateCount(filter: { type: intake }) @include(if: $includeForms)
            ccdaForms: patientFormTemplateCount(filter: { type: ccda }) @include(if: $includeCCDAForms)
            schedulingPlus: patientFormTemplateCount(filter: { type: preQualification })
              @include(if: $includeSchedulingPlus)
            formName: patientFormTemplateCount @include(if: $includeFormName)
            insurance: insuranceUniquePlanNamesCount @include(if: $includeInsurance)
            group: groupCount(filter: { type: user }) @include(if: $includeGroup)
            sender: userCount(filter: { type: staff, _operators: { name: { ne: null } } }) @include(if: $includeSender)
            approver: userCount(
              filter: { type: staff, _operators: { name: { ne: null }, roles: { in: ["manager", "admin"] } } }
            ) @include(if: $includeApprover)
            schedulerProvider: providerCount(
              filter: { visible: true, schedulable: true, isRenderingProvider: true, type: doctor }
            ) @include(if: $includeSchedulerProvider)
            trueTimeProvider: providerCount(
              filter: {
                visible: true
                schedulable: true
                isRenderingProvider: true
                isReferringProvider: false
                type: doctor
              }
            ) @include(if: $includeTrueTimeProvider)
          }
        `,
        {
          includeProvider,
          includeRenderingProvider,
          includeReferringProvider,
          includeFacility,
          includeCustomAction,
          includeApptType,
          includeCCDAForms,
          includeForms,
          includeSchedulingPlus,
          includeFormName,
          includeInsurance,
          includeGroup,
          includeSender,
          includeApprover,
          includeSchedulerProvider,
          includeTrueTimeProvider,
        },
      );

      const observableSubscription = queryObservable.subscribe({
        next: (counts) => stable_onSuccess({ ...counts }),
      });

      return () => observableSubscription.unsubscribe();
    }
  }, [
    isToQuery,
    enviroment,
    includeApptType,
    includeFacility,
    includeCustomAction,
    includeCCDAForms,
    includeForms,
    includeGroup,
    includeInsurance,
    includeProvider,
    includeReferringProvider,
    includeRenderingProvider,
    includeSchedulingPlus,
    includeFormName,
    includeSender,
    includeApprover,
    onSuccessRef,
    includeSchedulerProvider,
    includeTrueTimeProvider,
    shouldFetch,
  ]);
}
